'use client'

import { Tooltip, type TooltipHandle } from '@fukku/tooltip/Tooltip'
import { useDocumentDirection } from 'hooks/useDocumentDirection/useDocumentDirection'
import { useEffect, useRef, useState } from 'react'
import { usePricesStore } from '../../../context/PricesProvider'
import { BodyContent } from '../Content/BodyContent'
import { PRICES_TOOLTIP_TITLE } from '../Content/keys'
import type { PricesHistoryComponentProps } from '../ExtraInfo'
import { ExtraInfoButton } from '../ExtraInfoButton'

const MARGIN_TOOLTIP = 8

export function TooltipPricesHistory({
	pricesHistory,
	shouldShowDiscount,
	sendAnalytics,
}: Readonly<PricesHistoryComponentProps>) {
	const tooltipRef = useRef<TooltipHandle | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [tooltipShow, setTooltipShow] = useState<boolean>(false)
	const { isRtl } = useDocumentDirection()
	const { horizontal = false } = usePricesStore()

	useEffect(() => {
		if (tooltipShow) {
			tooltipRef.current?.open()
		} else {
			tooltipRef.current?.close()
		}
	}, [tooltipShow])

	const reverse = isRtl ? !horizontal : horizontal

	const boundary = {
		current: {
			getBoundingClientRect: () => ({
				left: 0,
				right:
					(containerRef.current?.getBoundingClientRect()?.right ?? 0) +
					MARGIN_TOOLTIP,
			}),
		} as HTMLDivElement,
	}

	const handleOnClick = () => {
		sendAnalytics()
		setTooltipShow(!tooltipShow)
	}

	return (
		<div ref={containerRef}>
			<Tooltip
				data-testid='tooltip.history.prices'
				ref={tooltipRef}
				boundary={boundary}
				preventHover={true}
				reverse={reverse}
				content={
					<BodyContent
						pricesHistory={pricesHistory}
						shouldShowDiscount={shouldShowDiscount}
						title={PRICES_TOOLTIP_TITLE}
					/>
				}
			>
				<ExtraInfoButton handleOnClick={handleOnClick} />
			</Tooltip>
		</div>
	)
}
